import { Injectable } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subject, filter } from 'rxjs';
import { AuthenticationService } from './authentication.service';
import { environment } from 'public/environments/environment';
import { SignalEvent, SignalEventType } from '../models/SignalModel';

@Injectable({
  providedIn: 'root'
})
export class SignalRService {
  private hubConnection!: signalR.HubConnection;
  private signalEvent!: Subject<any>;

  constructor(
    private authService: AuthenticationService,
    private snackBar: MatSnackBar,
    private translate: TranslateService
  ) {
    this.signalEvent = new Subject<any>();
  }

  public startConnection = () => {
    this.hubConnection = new signalR.HubConnectionBuilder()
      .withUrl(environment.signalR, {
        accessTokenFactory: () => this.authService.currentUserValue!.Token,
        transport: signalR.HttpTransportType.WebSockets
      }).build();

    this.hubConnection.start()
      .then((data) => {
        this.setupSignalREvents();
        this.authService.SignalRConnectionId.next(this.hubConnection.connectionId);
        // localStorage.setItem('connectionId', String(this.hubConnection.connectionId))
        console.log('Connection started ...')
      })
      .catch((err: any) => console.log('Error while starting connection ... ' + err));

  }
  stopConnection() {
    if (this.hubConnection) {
      this.hubConnection.stop()
        .then(() => console.log('Connection stopped ...'))
        .catch((err: any) => console.log('Error while stopping connection ... ' + err));
    }
  }
  /*
   * ***************************************************************
   * ***************************************************************
   * ***************************************************************
   * ***************************************************************
  */

  getDataStream<TDataShape>(...filterValues: SignalEventType[]): Observable<SignalEvent<TDataShape>> {
    return this.signalEvent.asObservable().pipe(filter(event => filterValues.some(f => f === event.type)));
  }

  private onMessage<TDataShape>(payload: SignalEvent<TDataShape>) {
    this.signalEvent.next(payload);
  }

  private setupSignalREvents() {
    this.hubConnection?.on('changeNotificationCount', (msgEn: string, msgAr: string) => {
      this.onMessage({ type: SignalEventType.changeNotificationCount, data: { msgEn, msgAr } });
      this.notify()
    });
    this.hubConnection?.on('evaluationTaskUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.evaluationTaskUpdate, data: { id } });
    });
    this.hubConnection?.on('commentUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.commentUpdate, data: { id } });
    });
    this.hubConnection?.on('monitorTaskUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.monitorTaskUpdate, data: { id } });
    });
    this.hubConnection?.on('evaluationRequestUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.evaluationRequestUpdate, data: { id } });
    });
    this.hubConnection?.on('verificationRequestUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.verificationRequestUpdate, data: { id } });
    });
    this.hubConnection?.on('verificationTaskUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.verificationTaskUpdate, data: { id } });
    });
    this.hubConnection?.on('productEvaluationTaskUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.productEvaluationTaskUpdate, data: { id } });
    });
    this.hubConnection?.on('ticketUpdate', (id: number, newTicketId: number, excludedEntityId: number) => {
      this.onMessage({ type: SignalEventType.ticketUpdate, data: { id, newTicketId, excludedEntityId } });
    });
    this.hubConnection?.on('refreshTickets', (ticketId: number) => {
      this.onMessage({ type: SignalEventType.refreshTickets, data: { ticketId } });
    });
    this.hubConnection?.on('commentsticketUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.commentsticketUpdate, data: { id } });
    });
    this.hubConnection?.on('fraudMonitoringRequestUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.fraudMonitoringRequestUpdate, data: { id } });
    });
    this.hubConnection?.on('fraudTaskUpdated', (id: number) => {
      this.onMessage({ type: SignalEventType.fraudTaskUpdated, data: { id } });
    });
    this.hubConnection?.on('rightUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.rightUpdate, data: { id } });
    });
    this.hubConnection?.on('certificateRequestUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.certificateRequestUpdate, data: { id } });
    });
    this.hubConnection?.on('surveyTaskUpdated', (id: number) => {
      this.onMessage({ type: SignalEventType.surveyTaskUpdated, data: { id } });
    });
    this.hubConnection?.on('surveyRequestUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.surveyRequestUpdate, data: { id } });
    });
    this.hubConnection?.on('invoiceOcrUpdate', (invoiceId: number) => {
      this.onMessage({ type: SignalEventType.invoiceOcrUpdate, data: { invoiceId } });
    });
    this.hubConnection?.on('invoiceOcrListUpdate', (invoiceId: number) => {
      this.onMessage({ type: SignalEventType.invoiceOcrListUpdate, data: { invoiceId } });
    });
    this.hubConnection?.on('sessionEnded', (id: number) => {
      this.onMessage({ type: SignalEventType.sessionEnded, data: { id } });
    });
    this.hubConnection?.on('consultationSessionUpdated', (id: number) => {
      this.onMessage({ type: SignalEventType.consultationSessionUpdated, data: { id } });
    });

    // Courses
    this.hubConnection?.on('newChatMessage', (id: number) => {
      this.onMessage({ type: SignalEventType.newChatMessage, data: { id } });
    });
    this.hubConnection?.on('submitteAssignmentScore', (id: number) => {
      this.onMessage({ type: SignalEventType.submitteAssignmentScore, data: { id } });
    });
    this.hubConnection?.on('liveSessionIsLive', (id: number) => {
      this.onMessage({ type: SignalEventType.liveSessionIsLive, data: { id } });
    });
    this.hubConnection?.on('newCourseUpdate', (id: number) => {
      this.onMessage({ type: SignalEventType.newCourseUpdate, data: { id } });
    });
    this.hubConnection?.on('courseOffline', (id: number) => {
      this.onMessage({ type: SignalEventType.courseOffline, data: { id } });
    });
    this.hubConnection?.on('courseOnline', (id: number) => {
      this.onMessage({ type: SignalEventType.courseOnline, data: { id } });
    });
    this.hubConnection?.on('assignmentReminder', (id: number) => {
      this.onMessage({ type: SignalEventType.assignmentReminder, data: { id } });
    });
    this.hubConnection?.on('consumerRefreshToken', (id: number) => {
      this.onMessage({ type: SignalEventType.consumerRefreshToken, data: { id } });
    });
    this.hubConnection?.on('consumerAiConversationCreated', (id: number) => {
      this.onMessage({ type: SignalEventType.consumerAiConversationCreated, data: { id } });
    });

  }

  /*******************************************************/
  notify(msg?: string) {
    this.snackBar.open(this.translate.instant('signal.You have a new notification'), '✖', {
      duration: 3000,
      horizontalPosition: 'end',
      verticalPosition: 'top'
    });
  }
}
